import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { Location, NgIf } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { catchError, of, Subscription, switchMap } from 'rxjs';
import { CdkScrollable } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import { StepClickedEvent } from '../../../shared/components/stepper/stepper.component';
import { LegalDocumentService } from '../../../shared/services/legal-document.service';
import { LocationService } from '../../../shared/services/location.service';
import { MillOfferNeed4Steel, State, PRICE_DEFAULT, LogisticsConstraints } from '../../models/need4steel';
import { Need4SteelWizardService, N4S_SERVICE } from '../../services/need4steel-wizard.service';
import { Need4SteelService } from '../../services/need4steel.api.service';
import { MillOfferService } from '../../services/mill-offer.service';
import { Need4SteelWizardStep } from '../../models/need4steel-wizard-state';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { SubmitDialogComponent } from '../../../shared/components/submit-dialog/submit-dialog.component';
import { FormNeed4SteelOffer } from '../../models/form-mill-offer';
import { ErrorService } from '../../../shared/services/error.service';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import {
	LegalDocumentAgreementComponent
} from '../../../shared/components/legal/legal-document-user-agreement/legal-document-agreement.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import {
	SubmissionFeedbackComponent
} from '../../../shared/components/submission-feedback/submission-feedback.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { ContactMailComponent } from '../contact-mail.component';
import { UNDERSCORE } from '../../../shared/utils/constant';

import { WizardStepperComponent } from './wizard-stepper/wizard-stepper.component';
import { WizardPageContent } from './wizard-page-content';

// by default scroll index have -2. if any issue found on header level it will changed to -1.
const DEFAULT_SCROLLINDEX = -2;
const DEFAULT_HEADER_SCROLLINDEX = -1;

@Component({
	selector: 'copo-wizard',
	templateUrl: './wizard.component.html',
	styleUrl: './wizard.component.css',
	imports: [
		ScrollerDirective,
		NgIf,
		SubmissionFeedbackComponent,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		LegalDocumentAgreementComponent,
		WizardStepperComponent,
		LoadingSpinnerComponent,
		RouterOutlet,
		MatIconModule,
		AfterViewInitDirective,
	],
})
export class WizardComponent implements OnInit {
	@ViewChild('scroller', { read: CdkScrollable }) scrollBar?: CdkScrollable;
	pageTitle = '';
	isLoading = true;
	iscontactMailError = true;
	isConfirmDisabled = true;
	isUserAgreementsLoading = true;
	deliveryWeek: number | undefined;
	previousPage: string | undefined;
	offerSubscription?: Subscription;
	millOfferById?: MillOfferNeed4Steel;
	millOfferId: string;
	state: State;
	pageContent: WizardPageContent | undefined;
	titleText?: string;
	priceValid?: boolean;
	notAgreedLegalDocumentIds: number[] = [];
	n4sServiceCode = N4S_SERVICE;
	n4sLabelValue: string;
	readonly form: FormNeed4SteelOffer;
	readonly UNDERSCORE = '─';
	readonly readOnlyUser?: boolean;

	constructor(
		readonly locationService: LocationService,
		private readonly need4SteelService: Need4SteelService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		readonly need4steelWizardService: Need4SteelWizardService,
		readonly location: Location,
		public millOfferService: MillOfferService,
		public errorService: ErrorService,

		private readonly dialog: MatDialog,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly legalDocumentService: LegalDocumentService

	) {
		this.previousPage = this.need4steelWizardService.actionToGoBack();
		if (!this.previousPage) {
			this.previousPage = this.need4steelWizardService.actionRetrievePreviousPage();
		}
		this.state = <State> location.getState();
		this.form = this.need4steelWizardService.getStateOfferForm();
		if (need4steelWizardService.isMillOfferIdPresent()) {
			this.millOfferId = String(this.millOfferService.getMillOfferId());
		} else {
			this.millOfferId = this.state.millOfferId || '';
			this.millOfferService.saveMillOfferId(this.millOfferId);
		}
		this.readOnlyUser = this.userService.user.readOnly;

		this.n4sLabelValue = this.translationService.getTR('legal-document.needforsteel.sales.framework');
	}

	onUserAgreementCheck(filterObject:
	{ agreedToSalesConditions: boolean; notAgreedLegalDocumentIds: number[]; isLoading: boolean }) {
		this.isUserAgreementsLoading = filterObject.isLoading;
		this.notAgreedLegalDocumentIds = filterObject.notAgreedLegalDocumentIds;
		this.isConfirmDisabled = Boolean(!filterObject.agreedToSalesConditions);
	}

	ngOnInit(): void {
		this.offerSubscription = this.need4SteelService.getMillOffer(
			this.millOfferId
		).pipe(
			switchMap(response => {
				this.isLoading = false;
				this.millOfferById = response;
				this.millOfferService.millOffer = this.millOfferById;
				if (this.millOfferById.subCategoryDescription && this.millOfferById.deliveryWeek
					&& this.millOfferById.deliveryYear) {
					this.titleText = this.translationService.getTR('arcelormittal.need4steel.entry');
					this.pageTitle = !this.millOfferService.millOffer.metalStud
						? `${ this.titleText } - ${ String(this.millOfferById.subCategoryDescription) }
							W${ String(this.millOfferById.deliveryWeek) }/${ String(this.millOfferById.deliveryYear) }`
						: `${ this.titleText } - ${ this.translationService.getTR('global-subcategory.MS') }
								${ String(this.millOfferById.subCategoryDescription) }
							W${ String(this.millOfferById.deliveryWeek) }/${ String(this.millOfferById.deliveryYear) }`;
				}
				this.deliveryWeek = this.millOfferById.deliveryWeek || undefined;
				if (this.millOfferById.millSiteCode) {
					return this.need4SteelService.getLogisticalroutes(this.millOfferById.millSiteCode).pipe(
						catchError((err: unknown) => {
							console.error('unable to load logistical routes', err);
							return of([]);
						})
					);
				}
				return of([]);
			})
		).subscribe({ next: response => {
			this.millOfferService.logisticalRoutes = <LogisticsConstraints[]>response;
		},
		error: async (err: unknown) => {
			this.isLoading = false;
			this.millOfferService.setOfferListErrorMsg(this.translationService.getTR('need4steel.offer.error.message'));
			console.error('unable to load mill offer', err);
			await this.router.navigate(['/need4steel/offerlist'], { replaceUrl: true });
		} });
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['/need4steel/wizard'], { relativeTo: this.route });
		}
	}

	getCurrentStatus(): string {
		return this.router.url;
	}

	async goToBackView() {
		this.need4steelWizardService.getState().stepIndex = 0;
		this.need4steelWizardService.getStateOfferForm().reset();
		await this.router.navigate(['/need4steel/offerlist'], { replaceUrl: true });
	}

	async goNextStep() {
		this.errorService.resetErrorMsg();
		this.form.reset(this.form.value);
		let isError = false;
		let scrollIndex = DEFAULT_SCROLLINDEX;
		// customer order number
		if (this.form.getFormCoNbr().invalid) {
			this.form.getFormCoNbr().markAsTouched();
			this.form.getFormCoNbr().updateValueAndValidity();
			scrollIndex = DEFAULT_HEADER_SCROLLINDEX;
			isError = true;
		}

		// sold to
		if (this.form.getFormSoldTo().invalid) {
			this.form.getFormSoldTo().markAsTouched();
			this.form.getFormSoldTo().updateValueAndValidity();
			scrollIndex = DEFAULT_HEADER_SCROLLINDEX;
			isError = true;
		}

		// ship to
		if (this.form.getFormShipTo().invalid) {
			this.form.getFormShipTo().markAsTouched();
			this.form.getFormShipTo().updateValueAndValidity();
			scrollIndex = DEFAULT_HEADER_SCROLLINDEX;
			isError = true;
		}

		const weightType = <string> this.form.getFormWeightType().value;
		if (this.millOfferService.productFormGroup) {
			const controlArray = <FormArray> this.millOfferService.productFormGroup.get('productSpecifications');
			let i = 0;
			for (const control of controlArray.controls) {
				const steelGradeControl = <FormControl> control.get('steelGrade');
				const thicknessControl = <FormControl> control.get('thickness');
				const widthControl = <FormControl> control.get('width');
				const maxCoilWeightControl = <FormControl> control.get('maxCoilWeight');
				const maxSpecificWeightControl = <FormControl> control.get('maxSpecificWeight');
				const coilControl = <FormControl> control.get('coil');
				const priceControl = <FormControl> control.get('price');
				// steelgrade
				if (steelGradeControl.invalid) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					steelGradeControl.markAsTouched();
					steelGradeControl.updateValueAndValidity();
					isError = true;
				}

				// thickness
				if (thicknessControl.invalid) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					thicknessControl.markAsTouched();
					thicknessControl.updateValueAndValidity();
					isError = true;
				}

				// width
				if (widthControl.invalid) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					widthControl.markAsTouched();
					widthControl.updateValueAndValidity();
					isError = true;
				}

				// maxCoilWeight
				if (weightType === 'maxNetWeightValue' && maxCoilWeightControl.invalid) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					maxCoilWeightControl.markAsTouched();
					maxCoilWeightControl.updateValueAndValidity();
					isError = true;
				}

				// maxSpecificWeight
				if (this.millOfferService.maxSpecCoilWeightOptionAvailable
					&& weightType === 'maxWeightPerWidthUnitValue'
					&& maxSpecificWeightControl.invalid) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					maxSpecificWeightControl.markAsTouched();
					maxSpecificWeightControl.updateValueAndValidity();
					isError = true;
				}

				// coil
				if (coilControl.invalid) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					coilControl.markAsTouched();
					coilControl.updateValueAndValidity();
					isError = true;
				}

				if (maxCoilWeightControl.invalid) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					maxCoilWeightControl.markAsTouched();
					maxCoilWeightControl.updateValueAndValidity();
					isError = true;
				}

				// price check
				if (!priceControl.value || priceControl.value === PRICE_DEFAULT) {
					if (scrollIndex === DEFAULT_SCROLLINDEX) {
						scrollIndex = i;
					}
					priceControl.markAsTouched();
					priceControl.updateValueAndValidity();
					control.markAllAsTouched();
					isError = true;
				}
				i++;
			}
		}
		this.scrollToRow(scrollIndex);
		if (isError) {
			this.pageContent?.checkError();
			return;
		}
		await this.need4steelWizardService.actionGoNextStep();
	}

	scrollToRow(index: number) {
		this.need4steelWizardService.scrollToItemRow(index);
	}

	onActivate(pageContent: WizardPageContent) {
		this.pageContent = pageContent;
		if (!this.scrollBar) {
			return;
		}
		const scrollPosition = this.need4steelWizardService.getState().scrollPositionByStep[this.pageContent.stepIndex];
		this.scrollBar.scrollTo({ top: scrollPosition });
	}

	onDeactivate() {
		if (!this.pageContent || !this.scrollBar) {
			return;
		}
		this.need4steelWizardService.getState().scrollPositionByStep[this.pageContent.stepIndex] =
			this.scrollBar.measureScrollOffset('top');
		this.pageContent = undefined;
	}

	isButtonNextVisible() {
		return !this.isLoading && this.need4steelWizardService.getState().stepIndex < Need4SteelWizardStep.SUMMARY;
	}

	isBtnProceedOrderVisible() {
		return !this.isLoading && this.need4steelWizardService.getState().stepIndex === Need4SteelWizardStep.SUMMARY;
	}

	async evtSubmitOrder() {
		const sub = this.dialog.open<SubmitDialogComponent, undefined, number>(
			SubmitDialogComponent,
			{ panelClass: 'copo-offer-dialog', disableClose: true }
		);
		this.errorService.resetErrorMsg();
		sub.componentInstance.progressMessage = this.translationService.getTR('progress.message');
		sub.componentInstance.waitMessage = this.translationService.getTR('wait.message');
		this.isLoading = true;
		for (const notAgreedLegalDocumentId of this.notAgreedLegalDocumentIds) {
			await this.legalDocumentService.saveUserAgreement(notAgreedLegalDocumentId);
		}

		this.iscontactMailError = false;
		await this.need4steelWizardService.actionSubmitOrder();
		this.isLoading = false;
		sub.close();
	}

	openContactMailDialog() {
		this.iscontactMailError = true;
		this.errorService.resetErrorMsg();
		this.dialog.open(ContactMailComponent, {
			data: {
				mill: this.millOfferById?.millSiteDescription || UNDERSCORE,
				offer: `${ String(this.millOfferById?.subCategoryDescription) }
							W${ String(this.millOfferById?.deliveryWeek) }/
							${ String(this.millOfferById?.deliveryYear) }`,
			},
			width: '99vw',
			maxHeight: '99vh',
			maxWidth: '650px'
		});
	}
}
